import { useTranslation } from "react-i18next";
import { useReputation } from "@/hooks/useReputation";

/**
 * A quest creator's reputation, read from the on-chain ledger.
 *
 * Discover used to print `public_profiles.reputation_overall` here, a column
 * nothing has ever written (see Dashboard.tsx), so every creator in the feed
 * read "0 rep": a verdict the chain never made. This reads the same ledger the
 * Profile card does.
 *
 * Renders nothing unless there is something true to say. Loading, a failed
 * read, and "never registered on this chain" all collapse to silence rather
 * than a number, because each of them rendered as a score states something
 * false about the creator. A registered creator with no history yet is "New".
 *
 * One read per distinct creator: useReputation is keyed on the user id, so a
 * feed of twenty quests from three creators costs three ledger reads.
 */
export function CreatorReputation({
  creatorId,
  variant,
}: {
  creatorId: string | null;
  /** `inline`: " · 96 rep" after the name on a card. `detail`: its own line. */
  variant: "inline" | "detail";
}) {
  // Split so no read is mounted without an id. useReputation(undefined) falls
  // back to the SESSION user, which here would print the viewer's own score
  // beside someone else's name.
  if (!creatorId) return null;
  return <LedgerReputation creatorId={creatorId} variant={variant} />;
}

function LedgerReputation({
  creatorId,
  variant,
}: {
  creatorId: string;
  variant: "inline" | "detail";
}) {
  const { t } = useTranslation();
  const view = useReputation(creatorId);

  if (view.status !== "active" && view.status !== "baseline") return null;

  const label =
    view.status === "baseline"
      ? t("bounty.creatorNew")
      : variant === "inline"
        ? `${Math.round(view.detail.overall)} ${t("common.rep")}`
        : t("bounty.reputationLabel", { score: Math.round(view.detail.overall) });

  if (variant === "inline") return <> · {label}</>;

  const quests = view.detail.questsCompleted;
  return (
    <p className="text-sm text-text-tertiary">
      {label}
      {quests > 0 && <> · {t("bounty.creatorQuestsCompleted", { count: quests })}</>}
    </p>
  );
}
